import { Card } from "@/components/podkit/Card";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { Text } from "@/components/podkit/typography/Text";
import { useMembership } from "@/hooks/use-membership";
import { useListAllRunnerSCMIntegrations } from "@/queries/runner-configuration-queries";
import { useListRunners, type UseListRunnersParams } from "@/queries/runner-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { RunnerKind, type SCMIntegration } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { useMemo, useState, type FC } from "react";
import { NewRunnerModal } from "./NewRunnerModal";
import { AddNewRunnerCard, RunnerCard } from "./RunnerCard";

export type RunnersListProps = {
    filter: UseListRunnersParams;
};

export const RunnersList: FC<RunnersListProps> = ({ filter }) => {
    const { data: user, isPending: isPendingUser } = useAuthenticatedUser();

    const useListRunnersFilter = { ...filter, creatorId: filter.kind === RunnerKind.LOCAL ? user?.id : undefined };
    const { data: runnersData, isPending: isPendingRunners } = useListRunners(useListRunnersFilter);
    const runners = runnersData || [];

    const membership = useMembership();
    const [showNewRunnerModal, setShowNewRunnerModal] = useState(false);

    const { data: integrations, isPending: integrationsPending } = useListAllRunnerSCMIntegrations(
        runners.map((runner) => runner.runnerId),
    );

    const integrationsByRunnerId = useMemo(() => {
        const integrationsByRunnerId = new Map<string, SCMIntegration[]>();
        for (const integration of integrations || []) {
            const runnerId = integration.runnerId;
            const runnerIntegrations = integrationsByRunnerId.get(runnerId) || [];
            runnerIntegrations.push(integration);
            integrationsByRunnerId.set(runnerId, runnerIntegrations);
        }
        return integrationsByRunnerId;
    }, [integrations]);

    const isPending = isPendingUser || isPendingRunners || integrationsPending;

    const showAddNewRunnerCard =
        filter.kind === RunnerKind.REMOTE && membership.membership?.userRole === OrganizationRole.ADMIN;

    if (isPending) {
        return <LoadingState />;
    }

    if (runners.length === 0 && !showAddNewRunnerCard) {
        return (
            <Card className="p-6 text-center">
                <Text className="mb-2 text-lg font-bold">No Runners Available</Text>
                <Text>There are currently no runners set up for this organization.</Text>
                <Text className="mt-2">Please contact your organization admin to set up runners.</Text>
            </Card>
        );
    }

    return (
        <div data-testid="runners-list">
            <div className="flex flex-wrap gap-6">
                {runners.map((runner) => (
                    <RunnerCard
                        runner={runner}
                        integrations={integrationsByRunnerId.get(runner.runnerId) || []}
                        key={runner.runnerId}
                    />
                ))}
                {showAddNewRunnerCard && <AddNewRunnerCard setShowNewRunnerModal={setShowNewRunnerModal} />}
                <NewRunnerModal open={showNewRunnerModal} onClose={() => setShowNewRunnerModal(false)} />
            </div>
        </div>
    );
};
